import React from 'react';
import { StepContainer } from '../StepContainer';
import { FormSection } from './form/formSection';
import { FormItem } from './form/formItem';
import { AreaInput } from '../../form';
import { BuildingDetailsInput, FinishingLevelSelect, AccessibilityInput, ConstraintsInput } from '../inputs';

interface FinitionsStepProps {
  data: any;
  onChange: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export const FinitionsStep: React.FC<FinitionsStepProps> = ({
  data = {
    finitionsDecoratives: {},
    menuiseriesDetails: {},
    eclairage: {},
    nettoyage: {}
  },
  onChange,
  onNext,
  onBack
}) => {
  const updateSection = (section: string, key: string, value: any) => {
    onChange({
      ...data,
      [section]: {
        ...data[section],
        [key]: value
      }
    });
  };

  return (
    <StepContainer
      title="Finitions"
      description="Détaillez les travaux de finition et de décoration"
      onNext={onNext}
      onBack={onBack}
    >
      <div className="space-y-6">
        {/* Informations générales */}
        <FormSection
          title="Informations générales"
          description="Caractéristiques du bien"
        >
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium mb-4">Surface</h3>
              <AreaInput
                value={data.area}
                onChange={(area) => onChange({ ...data, area })}
              />
            </div>

            {data.propertyType === 'building' && (
              <div>
                <h3 className="text-lg font-medium mb-4">Détails de l'immeuble</h3>
                <BuildingDetailsInput
                  value={data.buildingDetails || { units: 1, commonAreas: false }}
                  onChange={(buildingDetails) => onChange({ ...data, buildingDetails })}
                />
              </div>
            )}

            <div>
              <h3 className="text-lg font-medium mb-4">Niveau de finition</h3>
              <FinishingLevelSelect
                value={data.finishingLevel}
                onChange={(level) => onChange({ ...data, finishingLevel: level })}
              />
            </div>

            <div>
              <h3 className="text-lg font-medium mb-4">Accessibilité</h3>
              <AccessibilityInput
                value={data.accessibility}
                onChange={(accessibility) => onChange({ ...data, accessibility })}
              />
            </div>

            <div>
              <h3 className="text-lg font-medium mb-4">Contraintes et délais</h3>
              <ConstraintsInput
                value={data.constraints}
                onChange={(constraints) => onChange({ ...data, constraints })}
              />
            </div>
          </div>
        </FormSection>
        <FormSection
          title="1. Pose d'une cuisine"
          description="Installation d'une cuisine équipée"
        >
          <div className="space-y-4">
            <FormItem
              label="Kitchenette 120 cm (avec évier, 2 plaques de cuisson + 2 meubles hauts) - Premier prix"
              value={data.cuisine?.kitchenettepremier || { selected: false }}
              onChange={(value) => updateSection('cuisine', 'kitchenettepremier', value)}
              withQuantity
            />
            <FormItem
              label="Kitchenette 120 cm (avec évier, 2 plaques de cuisson + 2 meubles hauts) - Magasin de bricolage"
              value={data.cuisine?.kitchenettebrico || { selected: false }}
              onChange={(value) => updateSection('cuisine', 'kitchenettebrico', value)}
              withQuantity
            />
            <FormItem
              label="Cuisine 180/240 cm standard (avec meubles bas et haut, sans électroménager) - Premier prix"
              value={data.cuisine?.standardpremier || { selected: false }}
              onChange={(value) => updateSection('cuisine', 'standardpremier', value)}
              withQuantity
            />
            <FormItem
              label="Cuisine 180/240 cm standard (avec meubles bas et haut, sans électroménager) - Magasin de bricolage"
              value={data.cuisine?.standardbrico || { selected: false }}
              onChange={(value) => updateSection('cuisine', 'standardbrico', value)}
              withQuantity
            />
            <FormItem
              label="Cuisine 180/240 cm standard (avec meubles bas et haut, sans électroménager) - Cuisiniste"
              value={data.cuisine?.standardcuisiniste || { selected: false }}
              onChange={(value) => updateSection('cuisine', 'standardcuisiniste', value)}
              withQuantity
            />
            <FormItem
              label="Cuisine 180/240 cm standard (avec meubles bas et haut + électroménager) - Premier prix"
              value={data.cuisine?.standardelectropremier || { selected: false }}
              onChange={(value) => updateSection('cuisine', 'standardelectropremier', value)}
              withQuantity
            />
            <FormItem
              label="Cuisine 180/240 cm standard (avec meubles bas et haut + électroménager) - Magasin de bricolage"
              value={data.cuisine?.standardelectrobrico || { selected: false }}
              onChange={(value) => updateSection('cuisine', 'standardelectrobrico', value)}
              withQuantity
            />
            <FormItem
              label="Cuisine 180/240 cm standard (avec meubles bas et haut + électroménager) - Cuisiniste"
              value={data.cuisine?.standardelectrocuisiniste || { selected: false }}
              onChange={(value) => updateSection('cuisine', 'standardelectrocuisiniste', value)}
              withQuantity
            />
            <FormItem
              label="Cuisine 180/240 cm + îlot central ou placard toute hauteur 180 cm + électroménager - Premier prix"
              value={data.cuisine?.ilotpremier || { selected: false }}
              onChange={(value) => updateSection('cuisine', 'ilotpremier', value)}
              withQuantity
            />
            <FormItem
              label="Cuisine 180/240 cm + îlot central ou placard toute hauteur 180 cm + électroménager - Magasin de bricolage"
              value={data.cuisine?.ilotbrico || { selected: false }}
              onChange={(value) => updateSection('cuisine', 'ilotbrico', value)}
              withQuantity
            />
            <FormItem
              label="Cuisine 180/240 cm + îlot central ou placard toute hauteur 180 cm + électroménager - Cuisiniste"
              value={data.cuisine?.ilotcuisiniste || { selected: false }}
              onChange={(value) => updateSection('cuisine', 'ilotcuisiniste', value)}
              withQuantity
            />
          </div>
        </FormSection>

        <FormSection
          title="2. Menuiseries et détails décoratifs"
          description="Finitions sur menuiseries"
        >
          <div className="space-y-4">
            <FormItem
              label="Retouche peinture menuiseries"
              value={data.menuiseriesDetails?.retouchePeinture || { selected: false }}
              onChange={(value) => updateSection('menuiseriesDetails', 'retouchePeinture', value)}
              withLength
            />
            <FormItem
              label="Moulures décoratives"
              value={data.menuiseriesDetails?.mouluresDecoratives || { selected: false }}
              onChange={(value) => updateSection('menuiseriesDetails', 'mouluresDecoratives', value)}
              withLength
            />
          </div>
        </FormSection>

        <FormSection
          title="3. Éclairage et luminaires"
          description="Installation ou ajustement de luminaires (hors frais électriques)"
        >
          <div className="space-y-4">
            <FormItem
              label="Spots encastrés"
              value={data.eclairage?.spots || { selected: false }}
              onChange={(value) => updateSection('eclairage', 'spots', value)}
              withQuantity
            />
            <FormItem
              label="Suspensions"
              value={data.eclairage?.suspensions || { selected: false }}
              onChange={(value) => updateSection('eclairage', 'suspensions', value)}
              withQuantity
            />
            <FormItem
              label="Appliques murales"
              value={data.eclairage?.appliques || { selected: false }}
              onChange={(value) => updateSection('eclairage', 'appliques', value)}
              withQuantity
            />
          </div>
        </FormSection>

        <FormSection
          title="4. Nettoyage et livraison"
          description="Nettoyage et préparation pour livraison"
        >
          <div className="space-y-4">
            <FormItem
              label="Nettoyage des sols"
              value={data.nettoyage?.nettoyageSols || { selected: false }}
              onChange={(value) => updateSection('nettoyage', 'nettoyageSols', value)}
              withSurface
            />
            <FormItem
              label="Dépoussiérage"
              value={data.nettoyage?.depoussierage || { selected: false }}
              onChange={(value) => updateSection('nettoyage', 'depoussierage', value)}
              withSurface
            />
            <FormItem
              label="Enlèvement des gravats"
              value={data.nettoyage?.enlevementGravats || { selected: false }}
              onChange={(value) => updateSection('nettoyage', 'enlevementGravats', value)}
              withVolume
            />
            <FormItem
              label="Livraison du chantier clé en main"
              value={data.nettoyage?.livraisonCleEnMain || { selected: false }}
              onChange={(value) => updateSection('nettoyage', 'livraisonCleEnMain', value)}
            />
          </div>
        </FormSection>
      </div>
    </StepContainer>
  );
};

export default FinitionsStep;